/**
 * Card Match reducer.
 *
 * Pure function: takes a state + action, returns the next state.
 *
 * Scene routing:
 *   - LOBBY (no round, no reveal, no podium)  -> lobby
 *   - cm_round_open                            -> round
 *   - cm_round_end                             -> reveal
 *   - cm_game_over                             -> podium
 *   - WS connection lost                       -> connection_lost
 */

import type { OutMsg } from '@sg/shared';
import type { BrowserState, RoundState, SceneName, TileViewState } from './types.js';

export type Action =
  | { type: 'ws'; msg: OutMsg }
  | { type: 'connection'; value: BrowserState['connection'] }
  | { type: 'clear-flip'; positions: [number, number] }
  | { type: 'cm-finalize-reveal' };

function buildInitialState(): BrowserState {
  const s: Partial<BrowserState> = {};
  s.scene = 'lobby';
  s.connection = 'idle';
  s.tenantId = '';
  s.state = 'LOBBY';
  s.theme = null;
  s.leaderboard = [];
  s.leaderboardAllTime = [];
  s.sessionLeaderboard = [];
  s.round = null;
  s.reveal = null;
  s.podium = null;
  return s as BrowserState;
}

export function initialState(): BrowserState {
  return buildInitialState();
}

function buildEmptyTiles(total: number): TileViewState[] {
  const out: TileViewState[] = [];
  for (let i = 1; i <= total; i++) {
    out.push({ position: i, symbol: '', matched: false, peeking: false });
  }
  return out;
}

export function reduce(state: BrowserState, action: Action): BrowserState {
  if (action.type === 'connection') {
    if (state.connection === action.value) return state;
    const scene: SceneName = action.value === 'open' ? state.scene : 'connection_lost';
    return { ...state, connection: action.value, scene };
  }
  if (action.type === 'clear-flip') {
    return clearFlip(state, action.positions);
  }
  if (action.type === 'cm-finalize-reveal') {
    return finalizeReveal(state);
  }
  return reduceWs(state, action.msg);
}

/**
 * Handle cm_round_end. Instead of immediately swapping to the reveal scene,
 * we flip every unmatched tile face-up using the symbols from `full_board`
 * and stash a `pendingReveal` payload on the round. main.ts schedules a
 * `cm-finalize-reveal` action after a hold (~2.4s) so chat can see what
 * they missed before the reveal scene takes over. all_matched also goes
 * through this path — extra hold is harmless since every tile is already
 * face-up.
 */
function reduceRoundEnd(
  state: BrowserState,
  msg: Extract<OutMsg, { type: 'cm_round_end' }>,
): BrowserState {
  if (!state.round) {
    // Defensive — shouldn't happen, but if no round exists just transition
    // directly so we don't drop the reveal payload.
    return {
      ...state,
      scene: 'reveal',
      reveal: {
        index: msg.round_index,
        reason: msg.reason,
        winners: msg.winners.slice(),
        fullBoard: msg.full_board.slice(),
      },
    };
  }
  const tiles = state.round.tiles.slice();
  for (const t of msg.full_board) {
    const idx = t.position - 1;
    const cur = tiles[idx];
    if (!cur) continue;
    if (cur.matched) continue; // already permanently revealed
    tiles[idx] = { ...cur, peeking: true, symbol: t.symbol };
  }
  const pendingReveal = {
    index: msg.round_index,
    reason: msg.reason,
    winners: msg.winners.slice(),
    fullBoard: msg.full_board.slice(),
  };
  return {
    ...state,
    round: { ...state.round, tiles, flip: null, pendingReveal },
  };
}

/**
 * Transition from the round scene (with all tiles face-up) to the reveal
 * scene. Triggered by main.ts after a hold so chat can see the full board.
 * Inlines the reveal payload to keep the function under the param-count
 * lint cap.
 */
function finalizeReveal(state: BrowserState): BrowserState {
  const pending = state.round?.pendingReveal;
  if (!pending || !state.round) return state;
  return {
    ...state,
    scene: 'reveal',
    reveal: pending,
    round: { ...state.round, pendingReveal: null, flip: null },
  };
}

function reduceWs(state: BrowserState, msg: OutMsg): BrowserState {
  switch (msg.type) {
    case 'state_snapshot': {
      const s = msg.state;
      const isLobby = s.state.startsWith('LOBBY');
      const isPodium = s.state.startsWith('PODIUM');
      const inRound = state.round !== null || state.reveal !== null;
      const allowSceneSwitch = !inRound;
      const nextScene = allowSceneSwitch
        ? (isLobby ? 'lobby' : isPodium ? 'podium' : state.scene)
        : state.scene;
      return {
        ...state,
        tenantId: s.tenant_id,
        state: s.state,
        theme: s.theme ?? state.theme,
        leaderboard: s.leaderboard_top10 ?? state.leaderboard,
        leaderboardAllTime: s.leaderboard_all_time ?? state.leaderboardAllTime,
        round: isLobby && allowSceneSwitch ? null : state.round,
        reveal: isLobby && allowSceneSwitch ? null : state.reveal,
        podium: isLobby && allowSceneSwitch ? null : state.podium,
        scene: nextScene,
      };
    }
    case 'cm_round_open': {
      const round: RoundState = {
        index: msg.round_index,
        width: msg.grid_w,
        height: msg.grid_h,
        totalTiles: msg.total_tiles,
        pairCount: msg.pair_count,
        basePoints: msg.base_points,
        startsAtMs: msg.server_ts,
        endsAtMs: msg.ends_at_ms,
        countdownStartsAtMs: msg.countdown_starts_at_ms,
        tiles: buildEmptyTiles(msg.total_tiles),
        flip: null,
        roundScores: [],
      };
      return { ...state, scene: 'round', reveal: null, round };
    }
    case 'cm_tile_flip': {
      if (!state.round) return state;
      const tiles = state.round.tiles.slice();
      const aIdx = msg.positions[0] - 1;
      const bIdx = msg.positions[1] - 1;
      if (tiles[aIdx]) tiles[aIdx] = { ...tiles[aIdx]!, peeking: true, symbol: msg.symbols[0] };
      if (tiles[bIdx]) tiles[bIdx] = { ...tiles[bIdx]!, peeking: true, symbol: msg.symbols[1] };
      // Update round-score running total when a match awards points.
      let roundScores = state.round.roundScores;
      if (msg.match && typeof msg.points_awarded === 'number') {
        const pid = Number(msg.user_id);
        const existing = roundScores.find((r) => r.player_id === pid);
        if (existing) {
          roundScores = roundScores.map((r) =>
            r.player_id === pid ? { ...r, points: r.points + msg.points_awarded! } : r,
          );
        } else {
          roundScores = [
            ...roundScores,
            { player_id: pid, display_name: msg.display_name, points: msg.points_awarded },
          ];
        }
      }
      return {
        ...state,
        round: {
          ...state.round,
          tiles,
          roundScores,
          flip: {
            positions: msg.positions,
            symbols: msg.symbols,
            match: msg.match,
            pointsAwarded: msg.points_awarded,
            displayName: msg.display_name,
            peekEndsAtMs: msg.peek_ends_at_ms,
          },
        },
      };
    }
    case 'cm_tile_reveal_permanent': {
      if (!state.round) return state;
      const tiles = state.round.tiles.slice();
      for (const pos of msg.positions) {
        const idx = pos - 1;
        if (tiles[idx]) tiles[idx] = { ...tiles[idx]!, matched: true, peeking: false, symbol: msg.symbol };
      }
      return { ...state, round: { ...state.round, tiles, flip: null } };
    }
    case 'cm_round_end': {
      return reduceRoundEnd(state, msg);
    }
    case 'cm_session_leaderboard': {
      return {
        ...state,
        sessionLeaderboard: msg.rows.map((r) => ({
          rank: r.rank,
          player_id: r.player_id,
          display_name: r.display_name,
          total_points: r.total_points,
        })),
      };
    }
    case 'cm_board_state': {
      // Cumulative board snapshot. Used to rehydrate mid-round joiners and
      // as a defensive resync. Backend `positions[].index` is 0-indexed
      // (raw tile.index). Browser tile model is 1-indexed `position`, so
      // array index === msg.positions[i].index directly.
      if (!state.round) return state;
      // Build a Set of in-flight peek positions (1-indexed) to preserve them
      // — a snapshot that lands while a peek is on-screen must NOT flip the
      // peeking tiles back face-down. The live cm_tile_flip / clear-flip
      // stream owns peek state.
      const inFlightPeek = new Set<number>();
      if (state.round.flip) {
        inFlightPeek.add(state.round.flip.positions[0]);
        inFlightPeek.add(state.round.flip.positions[1]);
      }
      const tiles = state.round.tiles.slice();
      for (const snap of msg.positions) {
        const arrIdx = snap.index; // already 0-indexed
        const pos = snap.index + 1;
        const cur = tiles[arrIdx];
        if (!cur) continue;
        if (snap.state === 'matched') {
          // Matched is authoritative — paint symbol + matched, clear peek.
          tiles[arrIdx] = { ...cur, matched: true, peeking: false, symbol: snap.symbol };
        } else {
          // face_down — preserve in-flight peek, don't touch already-matched, otherwise leave the local tile alone.
          if (cur.matched) continue;
          if (inFlightPeek.has(pos)) continue;
          // Don't fabricate symbol changes — face-down snapshots are silent about
          // the tile's local view state.
          continue;
        }
      }
      // Update round scores from score_rows.
      const roundScores = msg.score_rows.map((r) => ({
        player_id: Number(r.user_id),
        display_name: r.display_name,
        points: r.points,
      }));
      return {
        ...state,
        round: { ...state.round, tiles, roundScores },
      };
    }
    case 'cm_game_over': {
      return { ...state, scene: 'podium', podium: msg.podium };
    }
    default:
      return state;
  }
}

/**
 * Clear the in-flight flip-pair after the peek expires.
 *
 * If the flip was a match: leave tiles untouched (still peeking) and just
 * drop the flip record. The later cm_tile_reveal_permanent frame promotes
 * peeking→matched, so we avoid a one-frame face-down flicker between this
 * clearFlip and the reveal.
 *
 * Otherwise (mismatch): flip the named positions back face-down — peeking
 * cleared and symbol blanked — unless they're already matched.
 */
function clearFlip(state: BrowserState, positions: [number, number]): BrowserState {
  if (!state.round) return state;
  if (state.round.flip?.match === true) {
    return { ...state, round: { ...state.round, flip: null } };
  }
  const tiles = state.round.tiles.slice();
  for (const pos of positions) {
    const idx = pos - 1;
    if (!tiles[idx]) continue;
    if (tiles[idx]!.matched) continue; // matched tiles stay revealed
    tiles[idx] = { ...tiles[idx]!, peeking: false, symbol: '' };
  }
  return { ...state, round: { ...state.round, tiles, flip: null } };
}
